import { FC } from "react";

import { Box } from "@mui/material";
import MarkdownIt from "markdown-it";
import sanitizeHtml from "sanitize-html";

import { useAssistantConfig } from "../../contexts/AssistantConfigContext";
import { AssistantLinkPolicy, isAllowedAssistantLink } from "../../helpers/isAllowedAssistantLink";

const markdown = new MarkdownIt({ html: false, linkify: false, breaks: true }).disable("image");

interface MarkdownProps {
  text: string;
}

export const Markdown: FC<MarkdownProps> = ({ text }) => {
  const { linkPolicy } = useAssistantConfig();
  return (
    <Box
      sx={(theme) => ({
        ...theme.typography.body2,
        color: theme.palette.text.neutral.intense,
        overflowWrap: "anywhere",
        "& > :first-child": { mt: 0 },
        "& > :last-child": { mb: 0 },
        "& p, & ul, & ol, & blockquote, & pre": { my: 1.5 },
        "& ul, & ol": { pl: 3 },
        "& li + li": { mt: 0.5 },
        "& h1, & h2, & h3, & h4, & h5, & h6": { ...theme.typography.subtitle2, mt: 2, mb: 1 },
        "& a": { color: theme.palette.text.brand.intense, textDecoration: "underline", textUnderlineOffset: "0.2em" },
        "& a:hover": { textDecorationThickness: "0.15em" },
        "& blockquote": { ml: 0, pl: 2, borderLeft: 3, borderColor: theme.palette.border.brand.quiet },
        "& code": { bgcolor: theme.palette.surface.neutral.supershy, borderRadius: 0.5, px: 0.5 },
        "& pre": { bgcolor: theme.palette.surface.neutral.supershy, borderRadius: 1, p: 1.5, overflowX: "auto" },
        "& pre code": { p: 0 },
        "& table": { display: "block", overflowX: "auto", borderCollapse: "collapse" },
        "& th, & td": { border: 1, borderColor: theme.palette.border.brand.quiet, p: 1, textAlign: "left" },
      })}
      dangerouslySetInnerHTML={{ __html: renderSafeMarkdown(text, linkPolicy) }}
    />
  );
};

const renderSafeMarkdown = (text: string, linkPolicy: AssistantLinkPolicy): string =>
  sanitizeHtml(markdown.render(text), {
    allowedTags: [
      "p",
      "br",
      "strong",
      "em",
      "s",
      "ul",
      "ol",
      "li",
      "blockquote",
      "code",
      "pre",
      "h1",
      "h2",
      "h3",
      "h4",
      "h5",
      "h6",
      "hr",
      "a",
      "table",
      "thead",
      "tbody",
      "tr",
      "th",
      "td",
    ],
    allowedAttributes: { a: ["href", "target", "rel"], ol: ["start"] },
    allowedSchemes: ["https", "http"],
    allowProtocolRelative: false,
    transformTags: { a: (tagName, attribs) => toSafeLink(tagName, attribs, linkPolicy) },
  });

const toSafeLink = (
  tagName: string,
  attribs: sanitizeHtml.Attributes,
  linkPolicy: AssistantLinkPolicy
): sanitizeHtml.Tag => {
  if (!attribs.href || !isAllowedAssistantLink(attribs.href, window.location.origin, linkPolicy)) {
    return { tagName: "span", attribs: {} };
  }
  return { tagName, attribs: { href: attribs.href, target: "_blank", rel: "noopener noreferrer" } };
};
